import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import { selectShopifySuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { Switch } from 'antd';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './ShopifyIntegrations.module.css';

type Props = {
  controlsDisabled: boolean;
  tokenType: 'api_key' | 'oauth_token';
  tokenId: number;
  activeFbPageId: number;
  withNavLink?: boolean;
};

export const ShopifySuppressionSettings = ({
  controlsDisabled,
  tokenType,
  tokenId,
  activeFbPageId,
  withNavLink = true,
}: Props) => {
  const { update, create, retrieveAll } = useSuppressionListsCRUDActions();
  const suppressions = useSelector(selectShopifySuppressionList);
  const suppression = suppressions[0];
  const updateSuppression = (enabled: boolean) => {
    const tokenKey =
      tokenType === 'api_key' ? 'shopify_api_key' : 'shopify_oauth';
    if (suppression?.id) {
      update(activeFbPageId, {
        id: suppression.id,
        enabled,
        sourceId: suppression.sourceId,
        sourceKind: tokenKey,
      });
    } else {
      create(activeFbPageId, {
        enabled,
        sourceId: tokenId,
        sourceKind: tokenKey,
      });
    }
  };

  useEffect(() => {
    if (suppressions.length === 0) {
      retrieveAll(activeFbPageId);
    }
  }, []);

  const scrollToElement = (selector: string) => {
    document.getElementById(selector)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const onNavLinkClick: React.MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault();
    const el = e.target as Element;
    const href = el?.getAttribute('href');
    if (href) {
      const withoutHash = href.split('#')[1];
      scrollToElement(withoutHash);
    }
  };

  return (
    <div
      className={classNames(styles.box, styles.suppressionBox, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={classNames(styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Automatically suppress Shopify contacts
            <Switch
              key={`switch-suppression-${
                suppression?.enabled ? 'enabled' : 'disabled'
              }`}
              defaultChecked={!!suppression?.enabled}
              checkedChildren="Yes"
              unCheckedChildren="No"
              className={styles.switch}
              onChange={ok => updateSuppression(ok)}
            />
          </div>
        </div>
      </div>
      {withNavLink && (
        <div className={styles.suppressionLinks}>
          <a
            className={styles.navLink}
            href="#contact-suppression"
            onClick={onNavLinkClick}
          >
            Go to Suppression Settings
          </a>
          <a
            className={styles.questionMarkHelpLink}
            target="_blank"
            rel="noopener noreferrer"
            href="https://customers.ai/help/l/en/article/uhl81gxos2-enable-email-suppression-list"
          >
            <QuestionCircleOutlined />
          </a>
        </div>
      )}
    </div>
  );
};
